import jsPDF from 'jspdf'
import { ScenarioData } from '../components/ScenarioCard'
import { HeaderData } from '../components/Header'

function fmt(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const isNeg = value < 0
  const abs = Math.abs(value)
  const str = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(abs)
  return isNeg ? `(${str})` : str
}

function fmtNum(value: number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

export function generatePdf(scenario: ScenarioData, header: HeaderData) {
  const doc = new jsPDF('p', 'mm', 'letter')
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 20
  const contentWidth = pageWidth - margin * 2
  let y = margin

  const colors = {
    dark: [30, 41, 59] as [number, number, number], // slate-800
    mid: [100, 116, 139] as [number, number, number], // slate-500
    light: [148, 163, 184] as [number, number, number], // slate-400
    green: [5, 150, 105] as [number, number, number], // emerald-600
    red: [220, 38, 38] as [number, number, number], // red-600
    black: [15, 23, 42] as [number, number, number], // slate-900
    lineGray: [226, 232, 240] as [number, number, number], // slate-200
  }

  // ── HEADER BAR ──
  doc.setFillColor(...colors.dark)
  doc.rect(0, 0, pageWidth, 42, 'F')

  doc.setTextColor(255, 255, 255)
  doc.setFontSize(20)
  doc.setFont('helvetica', 'bold')
  doc.text('Refinance Option Summary', margin, 16)

  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(180, 190, 210)
  doc.text(
    `${header.title}  •  ${header.loanType}  •  Priced ${header.datePriced}  •  Credit Score: ${header.creditScore}`,
    margin,
    25,
  )

  doc.setFontSize(10)
  doc.text(`Property Value: ${fmt(header.believedValue)}`, margin, 34)
  doc.text(
    `Current Rate: ${header.currentRate}  •  Current Payment: ${fmt(header.currentTotalPayment)}/mo`,
    margin + 80,
    34,
  )

  y = 52

  // ── SELECTED SCENARIO TITLE ──
  doc.setTextColor(...colors.black)
  doc.setFontSize(16)
  doc.setFont('helvetica', 'bold')
  doc.text(scenario.title, margin, y)

  doc.setFontSize(12)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...colors.mid)
  doc.text(
    `Rate: ${scenario.rate}`,
    margin + doc.getTextWidth(scenario.title) + 6,
    y,
  )
  y += 12

  // ── DIVIDER ──
  const drawDivider = () => {
    doc.setDrawColor(...colors.lineGray)
    doc.setLineWidth(0.3)
    doc.line(margin, y, pageWidth - margin, y)
    y += 6
  }

  // ── PAYMENT COMPARISON ──
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...colors.mid)
  doc.text('PAYMENT COMPARISON', margin, y)
  y += 8

  const newTotal =
    scenario.paymentBreakdown.newFirstPayment +
    scenario.paymentBreakdown.mortgageInsurance +
    scenario.paymentBreakdown.taxesInsurance

  // Current payment box
  doc.setFillColor(248, 250, 252)
  doc.roundedRect(margin, y - 4, contentWidth / 2 - 4, 28, 2, 2, 'F')

  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...colors.mid)
  doc.text('Current Monthly Payment', margin + 4, y + 2)
  doc.setFontSize(16)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...colors.black)
  doc.text(fmt(header.currentTotalPayment), margin + 4, y + 14)
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...colors.light)
  doc.text(
    `P&I: ${fmt(header.principalInterest)}  •  MI: ${fmt(header.mortgageInsurance)}  •  Tax/Ins: ${fmt(header.taxesInsurance)}`,
    margin + 4,
    y + 20,
  )

  // New payment box
  const rightX = margin + contentWidth / 2 + 4
  doc.setFillColor(236, 253, 245)
  doc.roundedRect(rightX - 4, y - 4, contentWidth / 2, 28, 2, 2, 'F')

  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...colors.green)
  doc.text('New Monthly Payment', rightX, y + 2)
  doc.setFontSize(16)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...colors.green)
  doc.text(fmt(newTotal), rightX, y + 14)
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(100, 160, 130)
  doc.text(
    `P&I: ${fmt(scenario.paymentBreakdown.newFirstPayment)}  •  MI: ${fmt(scenario.paymentBreakdown.mortgageInsurance)}  •  Tax/Ins: ${fmt(scenario.paymentBreakdown.taxesInsurance)}`,
    rightX,
    y + 20,
  )

  y += 34

  // ── MONTHLY SAVINGS HIGHLIGHT ──
  doc.setFillColor(236, 253, 245)
  doc.roundedRect(margin, y, contentWidth, 18, 2, 2, 'F')
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...colors.green)
  doc.text(
    `You save ${fmt(scenario.metrics.monthlySavings)} every month`,
    margin + 4,
    y + 7,
  )
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(80, 140, 110)
  doc.text(
    `That's ${fmt(scenario.metrics.annualSavings)}/year  •  ${fmt(scenario.metrics.fiveYearSavings)} over 5 years  •  ${fmt(scenario.metrics.thirtyYearSavings)} over 30 years`,
    margin + 4,
    y + 14,
  )
  y += 26

  drawDivider()

  // ── LOAN DETAILS ──
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...colors.mid)
  doc.text('LOAN DETAILS', margin, y)
  y += 8

  const detailRows = [
    ['New Loan Amount', fmt(scenario.paymentBreakdown.newFirstAmount)],
    ['New Rate', scenario.rate],
    ['Cash Needed', fmt(scenario.paymentBreakdown.cashNeeded)],
    ['Cash to Close', fmt(scenario.paymentBreakdown.cashToClose)],
  ]

  detailRows.forEach(([label, val]) => {
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...colors.mid)
    doc.text(label, margin, y)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...colors.black)
    const isNeg = val.startsWith('(')
    if (
      isNeg ||
      (label === 'Cash Needed' && scenario.paymentBreakdown.cashNeeded > 0)
    ) {
      doc.setTextColor(...colors.red)
    }
    doc.text(val, margin + contentWidth / 2, y)
    y += 6
  })

  y += 4
  drawDivider()

  // ── ESTIMATED COSTS ──
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...colors.mid)
  doc.text('ESTIMATED COSTS', margin, y)
  y += 8

  const costRows = [
    ['Misc Box A/B', scenario.estimatedCosts.miscBoxAB],
    ['Our Fee', scenario.estimatedCosts.ourFee],
    ['Discount/Rebate', scenario.estimatedCosts.discountRebate],
    ['Attorney/Title', scenario.estimatedCosts.attorneyTitle],
    ['Funding Fee', scenario.estimatedCosts.fundingFee],
  ]

  costRows.forEach(([label, val]) => {
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...colors.mid)
    doc.text(label as string, margin, y)
    doc.setFont('helvetica', 'bold')
    const numVal = val as number | null
    const isNeg = numVal !== null && numVal < 0
    doc.setTextColor(
      isNeg ? colors.red[0] : colors.black[0],
      isNeg ? colors.red[1] : colors.black[1],
      isNeg ? colors.red[2] : colors.black[2],
    )
    doc.text(fmt(numVal), margin + contentWidth / 2, y)
    y += 6
  })

  // Total cost line
  y += 2
  doc.setDrawColor(...colors.lineGray)
  doc.setLineWidth(0.5)
  doc.line(margin, y, pageWidth - margin, y)
  y += 6

  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...colors.black)
  doc.text('Total Closing Costs', margin, y)
  doc.text(fmt(scenario.estimatedCosts.totalCost), margin + contentWidth / 2, y)
  y += 10

  // Breakeven
  doc.setFillColor(248, 250, 252)
  doc.roundedRect(margin, y, contentWidth, 14, 2, 2, 'F')
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...colors.black)
  doc.text(
    `Breakeven Point: ${fmtNum(scenario.estimatedCosts.breakeven)} months`,
    margin + 4,
    y + 9,
  )
  y += 22

  // ── ADDITIONAL NOTES (Scenario 3) ──
  if (scenario.additionalNotes) {
    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...colors.mid)
    doc.text('FUTURE ADJUSTMENTS', margin, y)
    y += 8

    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...colors.black)
    doc.text(
      `Year 2 Payment: ${fmt(scenario.additionalNotes.year2Payment)}  (Save ${fmt(scenario.additionalNotes.year2Savings)}/mo)`,
      margin,
      y,
    )
    y += 6
    doc.text(
      `Year 3 Payment: ${fmt(scenario.additionalNotes.year3Payment)}  (Save ${fmt(scenario.additionalNotes.year3Savings)}/mo)`,
      margin,
      y,
    )
    y += 12
  }

  // ── FOOTER DISCLAIMER ──
  y = doc.internal.pageSize.getHeight() - 20
  doc.setDrawColor(...colors.lineGray)
  doc.setLineWidth(0.3)
  doc.line(margin, y, pageWidth - margin, y)
  y += 5
  doc.setFontSize(7)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...colors.light)
  doc.text(
    'This is a preliminary estimate. Actual rates, fees, and savings may vary based on final underwriting and market conditions.',
    margin,
    y,
    { maxWidth: contentWidth },
  )
  doc.text(
    `Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
    margin,
    y + 5,
  )

  // Save
  const safeName = scenario.title.replace(/[^a-zA-Z0-9]/g, '_')
  doc.save(`Refinance_Summary_${safeName}.pdf`)
}
